/**
 * Paper2Ink Web Auth, Library & Upload Manager (§7, SAAS-312).
 *
 * Implements:
 * - Session state verification (CSRF, /me, profile)
 * - Cloud Library listing, document creation, document opening, deletion
 * - Cloud revision snapshot creation with optimistic concurrency
 * - Real API file upload streaming and completion
 * - Offline-first draft preservation upon session expiration (HTTP 401)
 * - Safe JSON fallback export on expired sessions
 */

import type { Paper2InkApiClient } from "./client.ts";
import { ApiClientError } from "./client.ts";
import type {
  DocumentResponse,
  RevisionResponse,
  UploadResponse,
  UploadCompleteResponse,
  UserProfileResponse,
} from "./types.ts";
import { type CIMDocument, sanitizeCIMDocument } from "../editor/model.ts";
import type { DraftStore } from "../storage/draftStore.ts";
import { exportToJSON } from "../export/jsonExport.ts";

export interface AuthLibraryOptions {
  api: Paper2InkApiClient;
  draftStore: DraftStore;
  onSessionExpired?: (doc: CIMDocument, fallbackJson: string) => void;
  onStatusChange?: (message: string) => void;
}

export interface CloudOpenResult {
  document: DocumentResponse;
  revision: RevisionResponse | null;
  cimDocument: CIMDocument | null;
}

export class AuthLibraryManager {
  private readonly api: Paper2InkApiClient;
  private readonly draftStore: DraftStore;
  private currentUser: UserProfileResponse | null = null;
  private currentDocId: string | null = null;
  private currentHeadRevisionId: string | null = null;
  private onSessionExpired?: (doc: CIMDocument, fallbackJson: string) => void;
  private onStatusChange?: (message: string) => void;

  constructor(options: AuthLibraryOptions) {
    this.api = options.api;
    this.draftStore = options.draftStore;
    this.onSessionExpired = options.onSessionExpired;
    this.onStatusChange = options.onStatusChange;
  }

  public getCurrentUser(): UserProfileResponse | null {
    return this.currentUser;
  }

  public isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  public getCurrentDocId(): string | null {
    return this.currentDocId;
  }

  public setCurrentDocId(id: string | null): void {
    this.currentDocId = id;
  }

  public getCurrentHeadRevisionId(): string | null {
    return this.currentHeadRevisionId;
  }

  public setCurrentHeadRevisionId(revId: string | null): void {
    this.currentHeadRevisionId = revId;
  }

  /**
   * Check authentication and refresh CSRF token.
   */
  public async checkAuth(): Promise<{ authenticated: boolean; user: UserProfileResponse | null }> {
    try {
      try {
        await this.api.auth.csrf();
      } catch (err) {
        // CSRF fetch optional if unauthenticated
      }

      const user = await this.api.auth.me();
      this.currentUser = user;
      if (user.id) {
        this.draftStore.setUserId(user.id);
      }
      return { authenticated: true, user };
    } catch (err: any) {
      this.currentUser = null;
      return { authenticated: false, user: null };
    }
  }

  /**
   * Perform logout and reset user state without deleting local drafts.
   */
  public async logout(): Promise<void> {
    try {
      await this.api.auth.logout();
    } catch (err) {
      console.warn("Logout request warning:", err);
    } finally {
      this.currentUser = null;
      this.onStatusChange?.("Oturum kapatıldı.");
    }
  }

  /**
   * List cloud documents from the repository.
   */
  public async listDocuments(cursor?: string, limit: number = 20) {
    return await this.api.documents.list(cursor, limit);
  }

  /**
   * Create a new cloud document.
   */
  public async createDocument(title: string): Promise<DocumentResponse> {
    const doc = await this.api.documents.create({ title: title.trim() || "İsimsiz Belge" });
    this.currentDocId = doc.id;
    this.currentHeadRevisionId = doc.head_revision_id || null;
    return doc;
  }

  /**
   * Delete a cloud document by ID.
   */
  public async deleteDocument(documentId: string): Promise<void> {
    await this.api.documents.delete(documentId);
    if (this.currentDocId === documentId) {
      this.currentDocId = null;
      this.currentHeadRevisionId = null;
    }
  }

  /**
   * Delete the source image associated with a document.
   */
  public async deleteSource(documentId: string): Promise<void> {
    await this.api.documents.deleteSource(documentId);
  }

  /**
   * Open a cloud document and retrieve its head revision snapshot.
   */
  public async openDocument(documentId: string): Promise<CloudOpenResult> {
    const doc = await this.api.documents.get(documentId);
    this.currentDocId = doc.id;
    this.currentHeadRevisionId = doc.head_revision_id || null;

    if (!doc.head_revision_id) {
      return { document: doc, revision: null, cimDocument: null };
    }

    const rev = await this.api.documents.getRevision(documentId, doc.head_revision_id);
    const cimDoc = (rev.cim_document as unknown as CIMDocument) || null;
    if (cimDoc && cimDoc.source && !cimDoc.source.original_asset_id && doc.source_id && !cimDoc.source.source_deleted) {
      cimDoc.source.original_asset_id = doc.source_id;
    }
    return {
      document: doc,
      revision: rev,
      cimDocument: cimDoc,
    };
  }

  /**
   * Save a new revision of the active document to the cloud.
   * If session is expired (HTTP 401), automatically preserves active draft to IndexedDB.
   */
  public async saveCloudRevision(
    documentId: string,
    doc: CIMDocument,
    expectedHeadRevisionId?: string | null,
    branchKind: "conflict" | "conversion" | "mask_edit" | "manual" = "manual"
  ): Promise<RevisionResponse> {
    try {
      const rev = await this.api.documents.createRevision(
        documentId,
        {
          content: sanitizeCIMDocument(doc) as unknown as Record<string, unknown>,
          expected_head_revision_id: expectedHeadRevisionId || this.currentHeadRevisionId,
          mode: "advance_head",
          branch_kind: branchKind,
        },
        expectedHeadRevisionId || this.currentHeadRevisionId || undefined
      );
      this.currentDocId = documentId;
      this.currentHeadRevisionId = rev.id;
      this.onStatusChange?.(`Belge buluta kaydedildi (Revizyon: ${rev.id.substring(0, 8)}).`);
      return rev;
    } catch (err: any) {
      if (err instanceof ApiClientError && err.status === 401) {
        await this.preserveDraftOnExpired(doc, documentId);
      }
      throw err;
    }
  }

  /**
   * Upload an image file via the actual Section 7 API pipeline:
   * 1. POST /api/v1/uploads (initiate)
   * 2. PUT /api/v1/uploads/{id}/content (binary upload)
   * 3. POST /api/v1/uploads/{id}/complete (quarantine -> permanent asset)
   */
  public async uploadPhoto(
    file: { name: string; size: number; type: string; [key: string]: any },
    activeDocToPreserveOnExpired?: CIMDocument
  ): Promise<{ upload: UploadResponse; complete: UploadCompleteResponse }> {
    try {
      const upload = await this.api.uploads.initiate({
        filename: file.name,
        mime_type: file.type || "image/png",
        expected_bytes: file.size,
      });

      await this.api.uploads.uploadContent(upload.id, file as any, file.type || "image/png");

      const complete = await this.api.uploads.complete(upload.id);
      this.onStatusChange?.(`Dosya buluta yüklendi (Varlık ID: ${complete.asset_id.substring(0, 8)}).`);
      return { upload, complete };
    } catch (err: any) {
      if (err instanceof ApiClientError && err.status === 401 && activeDocToPreserveOnExpired) {
        await this.preserveDraftOnExpired(activeDocToPreserveOnExpired);
      }
      throw err;
    }
  }

  /**
   * Preserve active document to IndexedDB storage upon session expiration (SAAS-312).
   */
  public async preserveDraftOnExpired(
    doc: CIMDocument,
    docId?: string
  ): Promise<{ draftSaved: boolean; fallbackJson: string }> {
    const fallbackJson = exportToJSON(doc);
    let draftSaved = false;

    try {
      const saveRes = await this.draftStore.saveDraft(doc, {
        branchId: "main",
        isDirty: true,
      });
      draftSaved = saveRes.success;
    } catch (storeErr) {
      console.warn("Failed to store draft in draftStore:", storeErr);
    }

    this.onStatusChange?.("Oturum süresi doldu. Taslağınız yerel hafızada güvenle saklandı.");
    this.onSessionExpired?.(doc, fallbackJson);

    return { draftSaved, fallbackJson };
  }
}
